import type { AttributeSelection, ProductAttributeType } from '#types/product'
import type { Product, ProductVariants } from '~/api/clients/product/data-contracts'

/**
 * Builds availability/notify-me predicates for a PDP given the user's current attribute selection.
 * Returns three closures:
 * - `getAttrOptionAvailable(type, value)` - whether the variant matching the current selection
 *    plus `(type, value)` is in stock (defaults to `true` when no matching variant exists).
 * - `getAttrOptionNotifyMe(type, value)` - whether the matching variant exposes a notify-me CTA.
 * - `getMatchingVariant(type, value)` - the resolved variant itself, when all required attributes
 *    are picked.
 * @param attributeSelection - The user's current attribute picks (`color`, `size`, `length`, …).
 * @param details - Reactive reference to the loaded product details.
 * @returns Object with the three predicate / accessor functions.
 * @category Utils
 */
export function getProductAvailability(
  attributeSelection: AttributeSelection,
  details: Ref<Product | null>
) {
  const getAllVariantsNotifyMe = (variants: ProductVariants) => variants.every(({ notifyMe }) => notifyMe)

  const filterOtherAttributes = (attributeType: ProductAttributeType) => details.value?.attributes
    .filter((attribute) => !['color', 'fitType', attributeType].includes(attribute.type))
    .map((attribute) => attribute.type)

  const areAllAttributesSelected = (attributeTypes: ProductAttributeType[]) =>
    attributeTypes.every((type) => attributeSelection[type])

  const findMatchingVariant = (
    attributeTypes: ProductAttributeType[],
    type: ProductAttributeType,
    value: string
  ): ProductVariants[number] | undefined =>
    details.value?.variants.find((variant) =>
      attributeTypes.every((attributeType) =>
        variant.attributes[attributeType] === attributeSelection[attributeType])
      && variant.attributes[type] === value)

  const getMatchingVariant = (type: ProductAttributeType, value: string) => {
    const productAttributes = filterOtherAttributes(type) || []
    if (!productAttributes.length || !areAllAttributesSelected(productAttributes)) return
    return findMatchingVariant(productAttributes, type, value)
  }

  const getAttrOptionAvailable = (type: ProductAttributeType, value: string) => {
    const { productInventoryState } = getMatchingVariant(type, value) || {}
    return productInventoryState ? productInventoryState === 'InStock' : true
  }

  const getAttrOptionNotifyMe = (type: ProductAttributeType, value: string) => {
    const productAttributes = filterOtherAttributes(type) || []
    if (!productAttributes.length)
      return details.value?.variants.find(({ attributes }) => attributes[type] === value)?.notifyMe ?? false

    if (!areAllAttributesSelected(productAttributes))
      return details.value?.variants ? getAllVariantsNotifyMe(details.value.variants) : false

    return findMatchingVariant(productAttributes, type, value)?.notifyMe ?? false
  }

  return {
    getAttrOptionAvailable,
    getAttrOptionNotifyMe,
    getMatchingVariant
  }
}
